import axios from 'axios';
import { ModalProgrammatic as Modal, ToastProgrammatic as Toast } from 'buefy';
import dateFormat from 'dateformat';
import { defineStore } from 'pinia';
import Vue from 'vue';

import { DEFAULT_COLUMNS } from '@/utils/helpers/default-columns.js';
import { textDeSanitize } from '@/utils/helpers/functions.js';

// Component imports
import PdfPreview from '@/pages/Dashboard/components/PdfPreview.vue';
import { useLinkObject } from '@/pages/Dashboard/composables/LinkObject';
import TransferOwnership from '@/pages/Dashboard/components/TransferOwnership.vue';

// Other stores
import { useSettingsPageStore } from '@/pages/Settings/stores/settings-store.js';
import { useAnalyticsStore } from './analytics-store';
import { useHarbourStore } from './harbour-store';
import { useLibraryStore } from './library-store';
import { useWorkflowsStore } from './workflows-store';
import { useSuperdocStore } from './superdoc-store';
import { findRequestApprovalBlocksForLink } from '@/pages/Dashboard/components/grid_components/helpers';

export const useDashboardStore = defineStore('dashboard', {
  state: () => ({
    harbourStore: useHarbourStore(),
    libraryStore: useLibraryStore(),
    analyticsStore: useAnalyticsStore(),
    settingsStore: useSettingsPageStore(),
    workflowsStore: useWorkflowsStore(),
    superdocStore: useSuperdocStore(),
    gridReference: null,
    columnDefs: [],
    gridApi: null,
    gridColumnApi: null,
    currentOpenView: null, // dashboard, awaiting-my-review, signed-by-me
    status: 'Initializing...',
    title: 'All contracts',
    awaitingNodesToRemove: new Set(),
    MAX_LINKS: 20000,
    initialLinks: [],
    lastUpdate: null,
    
    // Priority loading coordination
    isLinksPriorityLoadingSetupComplete: false,
    priorityLinksLoadingResolver: null,

    // details grid
    detailsActiveView: {},

    // link totals
    totalLinks: 0,
    totalCompleted: 0,
    totalPending: 0,
    totalAwaitingMyReview: 0,
    totalSignedByMe: 0,

    // Live sessions
    liveSessions: [],
    liveSessionExpiries: {},

    hasLoadedLinksSincePageLoad: false,
    isLoadingDashboardData: false,
    isLinksReady: false,
    showQuickLinksSkeleton: false,
    workflowStatusCache: {},
    workflowStatusLoading: new Set(),

    signerBrands: [
      {
        domain: 'gmail.com',
        image:
          'https://upload.wikimedia.org/wikipedia/commons/thumb/7/7e/Gmail_icon_%282020%29.svg/512px-Gmail_icon_%282020%29.svg.png?20221017173631',
      },
    ],
    columnState: [],
    showAnalytics: false,

    // Filters
    mylinksCompleteState: 'ALL',
    adminState: 'defaultview',
    dashboardFiltersOptions: [
      { name: 'linkname', display_name: 'Link name' },
      { name: 'inputs', display_name: 'Agreement inputs' },
      { name: 'agreementlink', display_name: 'Agreement link' },
    ],
    textFilterValue: '',
    textFilterName: 'linkname',
    creatorEmailFilterValue: '',
    linksOriginalCreators: '',
    setSelectedFilterName: '',
    myLinksFilterSelectedAgreementId: null,
    myLinksFilterAvailableOrganizationalAgreements: [],

    currentFilterTitle: 'All',
    numDisplayedLinks: 0,
    getRowId: (params) => params.data.id,

    columnKeysToSave: ['colId', 'hide', 'width'],
    columnStateToSave: [],
    initialColsData: DEFAULT_COLUMNS,
    savedColsData: [],

    isSearchingServer: false,
    quickFilterTimeout: null,
    lastSearch: null,
    isLoadingView: false,

    // Link sessions
    linkSessions: [],
    inputSearchMatches: new Set(),

    // Dashboard previews
    isPreviewModalActive: false,
    previewLink: null,
    previewURL: null,
    previewDataURL: null,
    previewData: null,
    lastDashboardGroupId: null,

    // Right sidebar
    selectedLink: null,

    // Groups selected in Link created modal
    linksSelectedGroups: {},

    mainStatusesOrdered: [
      'completed',
      'completed-emails',
      'sent',
      'pending-signed',
      'pending-unsigned',
    ],
    secondaryStatusesOrdered: [],
    filterStatusesCache: new Map(),
  }),

  getters: {
    getTitle: (state) => {
      if (state.currentFilterTitle === 'All') return 'All contracts';
      else if (state.currentFilterTitle === 'Pending') return 'Pending contracts';
      else if (state.currentFilterTitle === 'Completed') return 'Completed contracts';
    },
  },

  actions: {
    // Cheap, string-valued summary of every raw field that feeds getLinkStatuses.
    // Used as the Status column value so AG Grid's change detection re-renders the
    // cell (and recomputes the filter value) whenever completion-related data changes.
    // Without a real column value, setRowData/applyTransaction delta updates leave the
    // Status cell stale (e.g. "Pending (0)" after submissions arrive from the enriched load).
    getLinkStatusFingerprint(linkData) {
      if (!linkData) return '';
      const authmode = linkData.auth_method?.authmode || '';
      const submissions = linkData.submissions?.length || 0;
      const completed = linkData.email_recipients_completed?.length || 0;
      const recipients = linkData.email_recipients?.length || 0;
      const isSent = linkData.is_sent ? 1 : 0;
      return `${authmode}|${submissions}|${completed}|${recipients}|${isSent}`;
    },

    getLinkStatuses(linkData) {
      const linkObject = useLinkObject(linkData);
      if (!linkObject) {
        return {
          main: 'pending-unsigned',
          secondary: null,
        };
      }

      // statuses mapped to criteria
      const criterionMap = {
        // Complete
        completed: () => Boolean(
          linkObject.getCompletionState.value === 'COMPLETED' &&
            linkObject.authMethod.value?.authmode !== 'EMAILS',
        ),

        // Complete (2)
        'completed-emails': () => Boolean(
          linkObject.getCompletionState.value === 'COMPLETED' &&
            linkObject.authMethod.value?.authmode === 'EMAILS',
        ),

        // Sent
        sent: () => Boolean(
          linkObject.isSent.value && linkObject.emailRecipientsCompleted.value.length === 0,
        ),

        // Pending (1/3)
        'pending-signed': () => Boolean(linkObject.authMethod.value?.authmode === 'EMAILS'),


        // Pending (0)
        'pending-unsigned': () => true, // catch all
      };

      // checking
      const checkStatus = (status) => criterionMap[status]();

      const mainStatus = this.mainStatusesOrdered.find(checkStatus) || null;
      const secondaryStatus = this.secondaryStatusesOrdered.find(checkStatus) || null;

      const newStatus = {
        main: mainStatus,
        secondary: secondaryStatus,
      };

      // if (Object.keys(this.linkStatusCache).length > 1000) this.linkStatusCache = {};
      // this.linkStatusCache[linkId] = newStatus;
      return newStatus;
    },

    // Apply user settings to filters
    setSavedDashboardSettings() {
      this.mylinksCompleteState = 'ALL';
    },

    onPdfPreviewClose() {
      this.isPreviewModalActive = false;
      this.previewLink = null;
      this.previewURL = null;
      this.previewData = null;
      this.previewDataURL = null;
    },

    getFirstSubmissionAsset() {
      const linkAssets = this.previewLink?.submissions;
      if (!linkAssets || !linkAssets.length) return null;
      const linkAssetsFirstAsset = linkAssets[0];
      return linkAssetsFirstAsset;
    },

    pdfPreviewEditClick(instance, props = {}) {
      if (!instance || !this.previewLink) return;
      this.previewLink.parent = instance.proxy;

      if (this.previewLink.superdocId) {
        this.superdocStore.openSuperdocFromDrafts({
          editorProps: props,
          parentComponent: instance,
          superdocId: this.previewLink.superdocId,
          agreementId: this.previewLink.agreementId,
          linkDisplayId: this.previewLink.id,
          linkObject: props.linkObject,
          referenceAttachments: props.referenceAttachments
        });
        return;
      }
      const ckeditorFileId = this.previewLink.ckeditor_agreement_id ||this.previewLink.ckeditorAgreementId;
      if(ckeditorFileId) {
        const upgradeProps = {
          docId: ckeditorFileId,
          linkId: this.previewLink.id,
          linkName: this.previewLink.linktitle,
          parent: this.previewLink.parent,
          orgName: this.harbourStore?.contextDict?.www_urlsubdomain
        }
        Vue.prototype.$openCkeditorUpgradeModal({props: upgradeProps});
        return;
      }

      this.harbourStore.openLinkInEditor(this.previewLink);
      this.onPdfPreviewClose();
    },

    async getPdfPreviewData() {
      const result = await axios({
        url: this.previewURL,
        method: 'GET',
        responseType: 'arraybuffer',
      });

      const blob = new Blob([result.data], { type: 'application/pdf' });
      const blobURL = URL.createObjectURL(blob);
      this.previewDataURL = blobURL;
    },

    isLinkCompleted(link) {
      const linkToCheck = link || this.previewLink;
      const auth = linkToCheck.auth_method?.authmode;
      if (auth !== 'EMAILS') return linkToCheck.submissions?.length > 0;
      return (
        linkToCheck.email_recipients_completed?.length === linkToCheck.email_recipients?.length
      );
    },

    openLinkBasedPdfPreview(linkObject) {
      this.previewLink = linkObject;
      const isCompleted = this.isLinkCompleted(linkObject);

      // Unsigned URL
      this.previewURL = `/agreepdfunsigned/${this.previewLink.id}`;

      // Is this completed? If so, get the final signed URL instead
      // This is the same regardless of PDF vs CK
      if (isCompleted) {
        const firstAsset = this.getFirstSubmissionAsset();
        if (firstAsset)
          this.previewURL = `/agreepdf/${firstAsset.id}/agreelink/${this.previewLink.id}`;
      }

      this.getPdfPreviewData();
      this.isPreviewModalActive = true;
    },

    async getMyLinkDataDetailsRequest({ linkDisplayId, signal = null }) {
      const url = 'data?agreebuilder-getmylinkdatadetails';
      const payload = {
        requesttype: 'agreebuilder-getmylinkdatadetails',
        agreedisplayid: linkDisplayId
      };
      const res = await this.$harbourData.post(url, payload, null, { signal });
      return res.data;
    },

    // Get the latest column data. Used by the column menu.
    updateColumns() {
      this.columnState = this.gridColumnApi.getColumnState();
    },

    /* Dashboard 'for me' counts */
    getAwaitingMyReviewCount() {
      const pending = this.harbourStore.myPendingContracts?.filter((link) => !link.awaitingMyReviewSigned);
      return pending?.length || null;
    },
    getSignedByMeCount() {
      return this.harbourStore.mySignedAgreements?.length || null;
    },

    updateCounts() {
      if (!this.gridApi) return;

      let totalLinks = 0;
      let totalCompleted = 0;
      let totalPending = 0;
      this.gridApi?.forEachNode((node) => {
        const { data } = node;

        // skip if current user is not creator and not admin view
        if (
          data.creator_email !== this.harbourStore.contextDict.systememail &&
          this.adminState !== 'adminview'
        ) return;

        if (!this.isLinkCompleted(data)) totalPending++;
        else totalCompleted++;
        totalLinks++;
      });
      this.totalLinks = totalLinks;
      this.totalCompleted = totalCompleted;
      this.totalPending = totalPending;

      // "for me" counts
      this.totalAwaitingMyReview = this.getAwaitingMyReviewCount();
      this.totalSignedByMe = this.getSignedByMeCount();
    },

    // Time display helper taken from previous mixin
    displayLocalDateTimeFromEpochMs(params) {
      const epochMs = params.value || params;
      let epoch_ms = epochMs;
      if (epochMs == 'NOW') epoch_ms = Date.now();
      try {
        const eventDate = new Date(epoch_ms);
        const options = {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
        };
        return eventDate.toLocaleTimeString('en-US', options);
      } catch (err) {
        return '-';
      }
    },

    // Filter functions
    agreementTypeValueGetter(params) {
      let { data } = params;
      let docxFile = data.ckeditor_agreement_id || data.superdoc_id;

      if (docxFile) {
        return 'DOCX';
      }

      return 'PDF';
    },

    // Column definitions
    generateColumns() {
      const emailRecipientsGetter = (params) => {
        const emails = new Set();
        params.data.email_recipients?.forEach((recipient) => {
          const isObject = !!recipient.recipientemail;
          emails.add(isObject ? recipient.recipientemail : JSON.parse(recipient)?.recipientemail);
        });
        params.data.submissions?.forEach((submission) => {
          emails.add(submission.signer_email);
        });
        return [...emails];
      };

      const customDateFilterOptions = [
        // custom equality
        {
          displayKey: 'equalByDay',
          displayName: 'Equals',
          predicate: (filterLocalDateAtMidnight, cellValue) => {
            const date = new Date(cellValue);
            const filterDate = new Date(filterLocalDateAtMidnight);

            return (
              date.getFullYear() === filterDate.getFullYear() &&
              date.getMonth() === filterDate.getMonth() &&
              date.getDate() === filterDate.getDate()
            );
          },
        },
          "notEqual",
          "lessThan",
          "lessThanOrEqual",
          "greaterThan",
          "greaterThanOrEqual",
        ]

      const columns = [
        {
          field: 'expander',
          cellRenderer: 'agGroupCellRenderer',
          headerName: '',
          headerClass: 'hidden',
          cellClass: 'expander',
          maxWidth: 30,
          minWidth: 30,
          resizable: false,
          filter: false,
          sortable: false,
          suppressMenu: true,
          suppressSizeToFit: true,
          suppressColumnsToolPanel: true,
          suppressMovable: true,
          pinned: this.harbourStore.isMobileView ? null : 'left',
        },
        {
          field: 'brand',
          headerName: '',
          headerClass: 'hidden',
          minWidth: 54,
          maxWidth: 54,
          pinned: this.harbourStore.isMobileView ? null : 'left',
          cellRenderer: 'BrandRenderer',
          resizable: false,
          filter: false,
          sortable: false,
          suppressMenu: true,
          suppressSizeToFit: true,
          suppressColumnsToolPanel: true,
          suppressMovable: true,
        },
        {
          field: 'title',
          headerName: 'Name',
          editable: (params) => {
            return !params.data.hasPendingApproval;
          },
          minWidth: 140,
          cellStyle: { cursor: 'text' },
          headerClass: 'hrbr-grid-cell-left-align',
          singleClickEdit: true,
          cellEditor: 'TitleEditor',
          cellEditorParams: {
            callback: this.updateLinkTitle,
          },
          valueGetter: (params) => textDeSanitize(params.data.title),
          filterValueGetter: (params) => params.data.title,
          suppressColumnsToolPanel: true,
          pinned: this.harbourStore.isMobileView ? null : 'left',
          suppressMovable: true,
          cellClass: 'limit-to-three-lines hrbr-grid-cell',
          hide: false,
          width: 250,
        },

        // Main columns
        {
          field: 'url',
          headerName: 'Link',
          cellRenderer: 'LinkRenderer',
          minWidth: 120,
          valueGetter: (params) => params.data.url,
          filterValueGetter: (params) => params.data.url,
          filterParams: {
            textFormatter: (value) => this._filterURL(value),
          },
          hide: false,
          width: 165,
        },
        {
          field: 'document',
          headerName: 'Doc',
          cellRenderer: 'ViewAgreementRenderer',
          minWidth: 85,
          maxWidth: 85,
          valueGetter: this.agreementTypeValueGetter,
          hide: false,
          width: 85,
        },
        {
          field: 'workflowsStatus',
          headerName: 'Workflows',
          minWidth: 150,
          cellRenderer: 'WorkflowStatusRenderer',
          cellStyle: { opacity: 1 },
          filterValueGetter: (params) => {
            const workflows = params.data.workflows_status || [];
            const filters = new Set();
            workflows.forEach((workflow) => {
              filters.add(workflow.workflow_name);
            });
            return Array.from(filters);
          },
          hide: false,
          width: 150,
        },
        {
          field: 'completionState',
          headerName: 'Status',
          cellRenderer: 'StateRenderer',
          filter: 'agSetColumnFilter',
          minWidth: 210,
          cellStyle: { opacity: 1 },
          hide: false,
          width: 500,
          // The value is a change-detection fingerprint, not a sortable label
          sortable: false,
          // Real cell value so delta row updates (setRowData with getRowId, applyTransaction)
          // refresh the Status cell when submissions / signers / sent state change.
          valueGetter: (params) => this.getLinkStatusFingerprint(params.data),
          filterValueGetter: (params) => {
            // check cache first (keyed by id + fingerprint so it invalidates on data changes)
            const cacheKey = `${params.data.id}|${this.getLinkStatusFingerprint(params.data)}`;
            const existingStatuses = this.filterStatusesCache.get(cacheKey);
            if (existingStatuses) return existingStatuses;
            
            const statuses = this.getLinkStatuses(params.data, 'filtervalue');
            // statuses to filter values
            const statusMap = {
              completed: 'Completed',
              'completed-emails': 'Completed',
              'pending-signed': 'Pending',
              sent: 'Sent',
              'pending-unsigned': 'Pending',
              approved: 'Approved',
              'approval-waiting-me': 'Review',
              'approval-waiting-other': 'Awaiting approval',
            };

            const mainStatusFilter = statusMap[statuses.main] || null;
            const secondaryStatusFilter = statusMap[statuses.secondary] || null;
            const newStatuses = [mainStatusFilter, secondaryStatusFilter];

            if (this.filterStatusesCache.size > 20000) this.filterStatusesCache.clear();
            this.filterStatusesCache.set(cacheKey, newStatuses);
            return newStatuses;
          },
        },
        {
          field: 'email_recipients',
          headerName: 'Signers',
          valueGetter: emailRecipientsGetter,
          filterValueGetter: emailRecipientsGetter,
          minWidth: 280,
          cellRenderer: 'SignersRenderer',
          cellClass: '',
          hide: false,
          width: 280,
        },
        {
          field: 'total_views',
          headerName: 'Activity',
          minWidth: 90,
          filter: 'agNumberColumnFilter',
          cellRenderer: 'ViewsRenderer',
          hide: true,
          width: 120,
        },
        {
          field: 'folder',
          cellRenderer: 'FolderRenderer',
          headerName: 'Folder',
          minWidth: 120,
          filterValueGetter: (params) => {
            const folderName = this.harbourStore.getFolderName(params.data.folder);
            return folderName;
          },
          hide: false,
          width: 120,
        },
        {
          field: 'authType',
          valueGetter: (params) => {
            const type = params.data?.auth_method?.authmode;
            if (type === 'EMAILS') return 'Email';
            else if (type === 'PASSCODE') return 'Password';
            else if (type === 'PUBLIC') return 'Public';
          },
          headerName: 'Auth type',
          minWidth: 120,
          hide: true,
          width: 120,
        },
        {
          field: 'templateTitle',
          valueGetter: (params) => {
            if (
              params.data.agreement_title &&
              (params.data.is_template || params.data.template_group_id)
            )
              return params.data.agreement_title;
          },
          headerName: 'Template',
          minWidth: 120,
          hide: true,
          width: 120,
        },
        {
          field: 'active',
          headerName: 'Link status',
          cellRenderer: 'ActiveToggleRenderer',
          minWidth: 130,
          filterValueGetter: (params) => {
            const active = params.data.active;
            if (active) return 'Active';
            else return 'Inactive';
          },
          valueGetter: (params) => (params.data.active ? 'ACTIVE' : 'INACTIVE'),
          hide: true,
          width: 130,
        },
        {
          field: 'last_update',
          valueFormatter: ({ value }) => this.harbourStore.formatUnixTimestamp(value),
          valueGetter: (params) => this.harbourStore.convertToUnixMillis(params.data.last_update),
          headerName: 'Last update',
          filterParams: {
            filters: [
              {
                filter: 'agDateColumnFilter',
                filterParams: {
                  filterOptions: customDateFilterOptions,
                }
              },
              {
                filter: 'agSetColumnFilter',
                filterParams: {
                  valueFormatter: (params) => this.harbourStore.formatUnixTimestamp(params.value),
                },
              },
            ],
          },
          minWidth: 200,
          wrapText: false,
          hide: true,
          width: 200,
        },
        {
          field: 'created',
          sort: 'desc',
          valueFormatter: ({ value }) => this.harbourStore.formatUnixTimestamp(value),
          valueGetter: (params) => this.harbourStore.convertToUnixMillis(params.data.created),
          filterParams: {
            filters: [
              {
                filter: 'agDateColumnFilter',
                filterParams: {
                  filterOptions: customDateFilterOptions,
                }
              },
              {
                filter: 'agSetColumnFilter',
                filterParams: {
                  valueFormatter: (params) => this.harbourStore.formatUnixTimestamp(params.value),
                },
              },
            ],
          },
          minWidth: 200,
          wrapText: false,
          headerName: 'Create date',
          hide: true,
          width: 200,
        },
        {
          field: 'creationDetails',
          minWidth: 150,
          headerName: 'Creator',
          cellRenderer: 'CreationRenderer',
          valueGetter: (params) => {
            const { creator_name, creator_email } = params.data;
            return [creator_name, creator_email];
          },
          filterValueGetter: (params) => {
            if (params.data.is_api_created) return 'via API';

            return params.data.creator_email;
          },
          hide: true,
        },
        {
          field: 'group_ids',
          minWidth: 200,
          headerName: 'Groups',
          cellRenderer: 'GroupsRenderer',
          filter: 'agSetColumnFilter',
          hide: false,
          filterValueGetter: (params) => {
            const groupIds = params.data.group_ids || [];
            const groupNames = groupIds.map((id) => {
              const group = this.settingsStore.listUserGroups.find((i) => i.id === id);
              return group?.name || null;
            });
            return groupNames;
          },
        },
        {
          field: 'actions',
          headerName: 'Actions',
          cellRenderer: 'DashboardActionsRenderer',
          minWidth: 260,
          maxWidth: 260,
          resizable: false,
          filter: false,
          sortable: false,
          suppressMenu: true,
          hide: false,
          suppressColumnsToolPanel: true,
        },

        // Hidden columns for filters only
        { field: 'creator_email', suppressColumnsToolPanel: true, filter: true, hide: true },
        { field: 'template_id', hide: true, suppressColumnsToolPanel: true },
        {
          field: 'signerEmails',
          getQuickFilterText: (params) => {
            const recipients = params.data.email_recipients || [];
            return recipients.map((recipient) => JSON.parse(recipient)?.recipientemail || '');
          },
          hide: true,
          suppressColumnsToolPanel: true,
        },
        {
          field: 'signerNames',
          getQuickFilterText: (params) => {
            const recipients = params.data.email_recipients || [];
            return recipients.map((recipient) => JSON.parse(recipient)?.fullname || '');
          },
          hide: true,
          suppressColumnsToolPanel: true,
        },
        { field: 'auth_method', hide: true, suppressMenu: true, suppressColumnsToolPanel: true },
        { field: 'id', hide: true, suppressMenu: true, suppressColumnsToolPanel: true },
        { field: 'searchMatch', hide: true, suppressMenu: true, suppressColumnsToolPanel: true },
      ];

      this.columnDefs = columns;
    },

    // Generate a saveable user column state array
    generateColumnStateToSave(gridColumnApi, grid, resetToDefaults = false) {
      const columns = gridColumnApi?.getColumnState();

      const defaultColumns = this.initialColsData.get(grid);
      const dataToSave = [];
      columns?.map((col) => {
        const stateToSave = {};
        const initialData = defaultColumns?.find((colData) => colData.colId === col.colId);

        this.columnKeysToSave?.forEach((key) => {
          if (resetToDefaults && initialData) {
            stateToSave[key] = initialData[key] !== undefined ? initialData[key] : col[key];
          } else stateToSave[key] = col[key];
        });
        dataToSave.push(stateToSave);
      });
      return dataToSave;
    },

    // Restore user-saved column state
    restoreUserColumnState(
      grid = 'dashboard',
      gridApi = this.gridApi,
      gridColumnApi = this.gridColumnApi,
    ) {
      const gridName = `grid-columns-${grid}`;
      // left fixed columns
      const fixedColNames = ['expander', 'brand'];

      // Ignore folders since that is global view now
      if (gridName === 'grid-columns-folders') return;

      if (gridName in this.harbourStore.savedSettings) {
        let state = this.harbourStore.savedSettings[gridName];

        const fixedCols = state
          .filter((col) => fixedColNames.includes(col.colId))
          .sort((a, b) => fixedColNames.indexOf(a.colId) - fixedColNames.indexOf(b.colId));
        const other = state.filter((col) => !fixedColNames.includes(col.colId));
        state = [...fixedCols, ...other];

        gridColumnApi?.applyColumnState({ state: state || [], applyOrder: true });
      }
    },

    resetGridApi() {
      this.gridApi = null;
      this.gridColumnApi = null;
      this.gridAssets = null;
    },

    // Save user's column layout to user preferences on the server
    async saveColumnLayout(gridColumnAPi, grid = 'dashboard', resetToDefault = false) {
      const state = this.generateColumnStateToSave(gridColumnAPi, grid, resetToDefault);
      const gridName = `grid-columns-${grid}`;

      this.harbourStore.savedSettings[gridName] = state || [];
      const options = {};
      options[gridName] = state;

      const result = await this.harbourStore.updateUserSettings(options);
      if (result?.status === 200) {
        Toast.open({
          duration: 2500,
          message: 'Column layout saved successfully',
          position: 'is-top',
          type: 'is-success',
        });
      } else {
        Toast.open({
          duration: 2500,
          message: 'Error while saving column layout. Try again in a moment.',
          position: 'is-top',
          type: 'is-warning',
        });
      }
      console.debug('Column layout saved successfully', result);
    },

    // Filter: By state (ALL, COMPLETE, PENDING)
    filterByState() {
      const filterInstance = this.gridApi?.getFilterInstance('completionState');
      if (!filterInstance) return;

      let filter = {
        type: 'set',
        values: [this.mylinksCompleteState],
      };
      this.mylinksCompleteState === 'ALL' && (filter = null);
      filterInstance.setModel(filter);
      this.gridApi.onFilterChanged();
    },

    setInitialView() {
      if (this.harbourStore.isAdmin) {
        this.adminState === 'adminview' ? this.setAdminView() : this.setDefaultView();
      } else {
        this.setDefaultView();
      }
    },

    resetViewFilters() {
      const filterNames = ['creator_email', 'group_ids'];
      filterNames.forEach((filterName) => {
        const filterInstance = this.gridApi?.getFilterInstance(filterName);
        if (!filterInstance) return;
        filterInstance.setModel(null);
      });
    },

    applyDateFilter(val) {
      const filterComponent = this.gridApi?.getFilterInstance('created');
      const filter = {
        filterType: "multi",
        filterModels: [{
          operator: "AND",
          conditions: [
            {
              dateFrom: val[0] ? dateFormat(val[0], 'yyyy-mm-dd HH:MM:ss') : '',
              dateTo: null,
              filterType: "date",
              type: "greaterThanOrEqual"
            },
            {
              dateFrom: null,
              dateTo: val[1] ? dateFormat(val[1], 'yyyy-mm-dd HH:MM:ss') : '',
              filterType: "date",
              type: "lessThanOrEqual"
            }
          ]
        }]
      }
      filterComponent.setModel(filter);
      this.gridApi.onFilterChanged();
    },

    setDashboardFilter(groupId = null) {
      if (this.adminState === 'adminview') this.setAdminView();
      else if (this.adminState === 'groupadminview') this.setGroupAdminView(groupId);
      else this.setDefaultView();
    },

    setGroupAdminView(groupId = null) {
      this.resetViewFilters();

      const filterInstance = this.gridApi?.getFilterInstance('group_ids');
      if (!filterInstance) return;

      this.adminState = 'groupadminview';
      this.gridColumnApi?.setColumnVisible('creationDetails', true);

      const filter = {
        type: 'set',
        values: [groupId],
      };

      filterInstance.setModel(filter);
      this.gridApi.onFilterChanged();
    },

    setAdminView() {
      this.resetViewFilters();

      const filterInstance = this.gridApi?.getFilterInstance('creator_email');
      if (!filterInstance) return;
      this.adminState = 'adminview';
      this.gridColumnApi?.setColumnVisible('creationDetails', true);
      filterInstance.setModel(null);
      this.gridApi.onFilterChanged();
    },

    setDefaultView() {
      this.resetViewFilters();

      const filterInstance = this.gridApi?.getFilterInstance('creator_email');
      if (!filterInstance) return;
      this.adminState = 'defaultview';
      const userEmail = this.harbourStore.contextDict?.systememail;
      if (!userEmail) return;

      let filter = {
        type: 'set',
        values: [userEmail],
      };

      // Status of whether or not the user has the 'creator' column saved in their column settings
      if (this.harbourStore.savedSettings['dashboard']) {
        const hasCreatorColumnVisible = !!this.harbourStore.savedSettings[
          'grid-columns-dashboard'
        ].find((column) => column.colId === 'creationDetails' && !column.hide);

        this.gridColumnApi?.setColumnVisible('creationDetails', hasCreatorColumnVisible);
      }

      filterInstance?.setModel(filter);
      this.gridApi?.onFilterChanged();
    },

    _filterURL(text) {
      try {
        const url = new URL(text);
        return url.pathname.split('/').pop();
      } catch (e) {
        return text;
      }
    },

    resetFilterById() {
      this.isSearchingServer = false;
      const filterInstance = this.gridApi?.getFilterInstance('id');
      if (!filterInstance) return;
      filterInstance.setModel(null);
      this.gridApi?.onFilterChanged();
    },

    async filterByInputValues() {
      try {
        if (this.isSearchingServer || !this.textFilterValue) return;
        const gridApi = this.gridApi;
        if (!gridApi) return;

        this.lastSearch = this.textFilterValue;
        this.isSearchingServer = true;
        this.resetSearchMatches();

        const options = {
          requesttype: 'assetlibrary-getmylinks',
          linkstatefiltervalue: 'ALL',
          textfiltervalue: this.textFilterValue,
          emailfiltervalue: null,
          displayidfiltervalue: null,
          agreementidfiltervalue: null,
          sortvalueorder: 'UPDATED-DESC',
          isadminview: this.harbourStore.isAdmin && this.adminState === 'adminview',
        };

        const pageOptions = [
          // first 100 rows
          {
            ...options,
            requestresultsoffset: 0,
            requestresultslimit: 100,
          },
          // everything after
          {
            ...options,
            requestresultsoffset: 100,
            requestresultslimit: null,
          },
        ];

        const processResults = (result, pageNumString) => {

          result.data?.forEach((match) => {
            const link = gridApi.getRowNode(match)?.data;
            if (!link) return;
            this.inputSearchMatches.add(link.id);
            link.searchMatch = this.textFilterValue;
          });

          this.gridApi?.onFilterChanged();

          const pageSize = result.data.length; 
          console.log("count filtered by input values:", pageSize, "- page:", pageNumString)
          return pageSize;
        };

        const firstPage = await Vue.prototype.$harbourData.post(
          `/core/inputsearch/`,
          pageOptions[0],
        );
        const resultsCount = processResults(firstPage, 'first');
        this.isSearchingServer = false; // unblock user input whether or not final page needed

        if (resultsCount < 100) return; // all results found

        const finalPage = await Vue.prototype.$harbourData.post(
          `/core/inputsearch/`,
          pageOptions[1],
        );
        processResults(finalPage, 'final');
      } catch (error) {
        console.error('Error searching inputs:', error);
      }
    },

    async resetSearchMatches() {
      if (this.inputSearchMatches.size === 0) return;
      this.inputSearchMatches = new Set();

      const gridApi = this.gridApi;
      if (!gridApi) return;

      gridApi.forEachNode((node) => {
        node.data.searchMatch = '';
      });
    },

    // Filter: Apply quick filter to dashboard main grid
    updateTextFilters(currentText) {
      this.lastSearch = null;
      if (!currentText) {
        this.textFilterValue = null;
        this.gridApi?.setQuickFilter(null);
        return;
      }

      this.textFilterValue = currentText;
      if (!this.textFilterValue) return;

      clearTimeout(this.quickFilterTimeout);
      this.quickFilterTimeout = setTimeout(() => {
        let filterText = this.textFilterValue;

        if (!this.textFilterValue) {
          return;
        }
        // If this is a URL, attempt to extract the ID to match the URL column by display id regardless of url
        if (
          this.textFilterValue.startsWith('http://') ||
          this.textFilterValue.startsWith('https://')
        ) {
          filterText = this._filterURL(this.textFilterValue);
        }
        this.gridApi?.setQuickFilter(filterText);
      }, 300);
    },

    // Updating filter for awaiting my review grid
    updateAwaitingMyReviewFilter(currentText) {
      if (!currentText) {
        this.textFilterValue = null;
        this.gridApi?.setQuickFilter(null);
        return;
      }

      this.textFilterValue = currentText;
      clearTimeout(this.quickFilterTimeout);
      this.quickFilterTimeout = setTimeout(() => {
        let filterText = this.textFilterValue;
        this.gridApi?.setQuickFilter(filterText);
      }, 200);
    },

    // Filter: By template ID
    filterByTemplate(id) {
      const filterInstance = this.gridApi?.getFilterInstance('templateId');
      if (!filterInstance) return;

      let filter = {
        type: 'set',
        values: [id],
      };
      if (id.toLowerCase() === 'all') {
        filter = null;
        this.myLinksFilterSelectedAgreementId = null;
      }
      filterInstance.setModel(filter);
      this.gridApi.onFilterChanged();
    },

    // Master/detail programmatic toggle
    expandDetails(id, openToPane = null) {
      const node = this.gridApi.getRowNode(id);
      if (node) {
        if (openToPane === 'live') node.data.live = true;
        node.setExpanded(!node.expanded);
      }
    },

    // Helpers - this was copied from the previous dashboard unchanged
    // should refactor for clarity
    // This method is not used in the code (probably not needed).
    copyToClipboard(linkDisplayId, linkSubdomain) {
      let shareLinkValue = '';
      let domain = 'hrbr.co/';
      // let subdomain = '';
      // if (linkSubdomain) {
      //   subdomain = `${linkSubdomain}.`;
      // }

      const locationHref = window.location.href;
      const protocol = 'https';

      if (locationHref.includes('localhost')) {
        const localBase = 'http://localhost:9090';
        shareLinkValue = `${localBase}/agree/${linkDisplayId}`;
      } else if (locationHref.includes('127.0.0.1')) {
        const loc = window.location;
        const localBase = `${loc.protocol}//${loc.hostname}${loc.port ? `:${loc.port}` : ''}`;
        shareLinkValue = `${localBase}/agree/${linkDisplayId}`;
      } else if (locationHref.includes('-staging.')) {
        shareLinkValue = `https://new-staging.myharbourshare.com/agree/${linkDisplayId}`;
      } else if (locationHref.includes('harbour-prod-webapp.uc.r.appspot.com')) {
        const [localBase] = locationHref.split('appspot.com');
        shareLinkValue = `${localBase}appspot.com/agree/${linkDisplayId}`;
      } else {
        // shareLinkValue = `${protocol}://${subdomain}${domain}${linkDisplayId}`;
        shareLinkValue = `${protocol}://${domain}${linkDisplayId}`;
      }

      navigator.clipboard.writeText(shareLinkValue).then(
        () => {
          Toast.open({
            duration: 2500,
            message: 'Success - agreement url copied to clipboard',
            position: 'is-top',
            type: 'is-success',
          });
        },
        () => {
          Toast.open({
            message: 'Sorry, failed to copy agreement url to clipboard',
            type: 'is-danger',
            position: 'is-top',
            duration: 3500,
          });
        },
      );
    },

    // Update the link title on server
    async updateLinkTitle(originalValue, newTitle, link_id) {
      const key = 'title';
      const options = {
        update_request: {
          value: newTitle,
          display_id: link_id,
        },
      };
      const result = await Vue.prototype.$harbourData.put('/core/myLinks/update/title', options);
      return this.verifyResult(originalValue, result, key, link_id);
    },

    // Update the link parent folder on server
    async updateLinkFolder(folder, display_id) {
      // Update the link folder on the server
      const value = folder.id;
      const options = {
        update_request: {
          value,
          display_id,
        },
      };
      const result = await Vue.prototype.$harbourData.put('/core/myLinks/update/folder', options);
      return result;
    },

    // Verify the result of a server update or rollback if necessary
    verifyResult(originalValue, result, key, itemId) {
      // If result is not 200, revert the change on the item
      if (result.status !== 200) {
        const item = this.gridApi.getRowNode(itemId);
        item[key] = originalValue;
        return false;
      }
      return true;
    },

    async transferLinkOwnership(linkObject) {
      Modal.open({
        component: TransferOwnership,
        props: {
          linkObject,
        },
      });
    },
    // Delete a link from the dashboard
    async deleteLink(linkObject) {
      // Confirm deletion
      const confirmation = `Are you certain you want to delete this link ("${linkObject.title}")?`;
      if (!confirm(confirmation)) return false;

      // Send request
      Vue.prototype.$harbourData.post('data?agreebuilder-deletemylink', {
        requesttype: 'agreebuilder-deletemylink',
        agreedisplayid: linkObject.id,
      });

      // Remove from from display right away
      this.gridApi.applyTransaction({
        remove: [linkObject],
      });
    },

    linkHasEditingDisabled(params) {
      const { data } = params;
      const linkObject = data;

      // completion state
      const isCompletedByAll = this.isLinkCompleted(linkObject);
      const isCompletedBySome = Boolean(linkObject.email_recipients_completed?.length);
      const isCompleted = isCompletedByAll || isCompletedBySome;

      // approval state
      const hasPendingApproval = this.linkHasPendingApproval(params);

      const editingDisabled = isCompleted || hasPendingApproval;
      return editingDisabled;
    },

    linkHasPendingApproval(params) {
      const linkId = params.data?.id;
      const workflowStatus = this.workflowStatusCache[linkId];

      const isLinkComplete = this.isLinkCompleted(params.data);
      if (params.data?.is_locked) return true;
      if (isLinkComplete) return null;
      if (!params.data?.has_approval) return false;
      if (!workflowStatus) return true;

      const foundRequestApprovalBlocks = findRequestApprovalBlocksForLink(
        workflowStatus,
        linkId,
      ).filter((block) => !!block.state.state);
      if (foundRequestApprovalBlocks.length > 0) {
        const hasIncompleteApproval = foundRequestApprovalBlocks.some((block) => block.state.state !== 'completed');
        return hasIncompleteApproval;
      } else {
        return false;
      }
    },

    getDomainFromEmail(email) {
      const regex = /@(.+)/;
      const matches = regex.exec(email);
      if (matches && matches.length > 1) {
        return matches[1];
      }
      return null;
    },

    openPresentationPdf(linkObject, src, parent) {
      // Open PDF preview
      const isCompleted = this.isLinkCompleted(linkObject);
      const completedSigners = linkObject.email_recipients_completed?.length;

      const isEditable = !isCompleted && !completedSigners;
      Modal.open({
        parent,
        component: PdfPreview,
        canCancel: true,
        hasModalCard: false,
        fullScreen: true,
        props: {
          tgturl: src,
          isEditable: isEditable,
          linkDisplayIdToEdit: linkObject.id,
          parent: this,
        },
        events: {
          close: () => {
            console.debug('Closing PDF preview');
          },
        },
      });
    },

    dashboardGridAutoSize() {
      setTimeout(() => {
        const gridReference = this.gridReference;
        const gridApi = this.gridApi;
        const gridColumnApi = this.gridColumnApi;
        this.harbourStore.autosizeGridByReference(gridReference, gridApi, gridColumnApi);
      }, 0);
    },

    autoSizeDashboardByPercent() {
      // Resize the dashboard according to column width percentages to maintain aspect ratio based on
      // User settings, if any
      const gridElement = this.gridReference?.$el;
      const skipColumns = ['expander', 'brand', 'document', 'actions'];
      const visibleColumns = this.gridColumnApi?.getAllDisplayedColumns();

      if (!visibleColumns || !gridElement) return;

      // Get the widths
      const visibleWidth = visibleColumns.reduce((acc, column) => acc + column.actualWidth, 0);
      const staticColumns = visibleColumns.filter((column) => skipColumns.includes(column.colId));
      const staticWidth = staticColumns.reduce((acc, column) => acc + column.actualWidth, 0);
      const availableWidth = gridElement.getBoundingClientRect().width - 20;

      // Get a list of resizeable columns
      const columnsToResize = visibleColumns.filter(
        (column) => !skipColumns.includes(column.colId),
      );
      const newSizes = [];
      columnsToResize.forEach((column) => {
        const widthPercent = column.actualWidth / (visibleWidth - staticWidth);
        const newWidth = (availableWidth - staticWidth) * widthPercent;
        const newColWidth = {
          key: column,
          newWidth,
        };
        newSizes.push(newColWidth);
      });
      this.gridColumnApi.setColumnWidths(newSizes);
    },

    async removeDraft({ id, name }) {
      //Confirm?
      const confirmation = confirm(
        'Are you sure you want to fully delete the draft ("' + name + '")?',
      );
      if (!confirmation) return false;

      //Remove
      const sourceItems = this.harbourStore.myDrafts;
      const itemIndex = sourceItems.findIndex((item) => item.id === id);
      sourceItems.splice(itemIndex, 1);
      Toast.open({
        duration: 2500,
        message: 'Success - draft removed',
        position: 'is-top',
        type: 'is-success',
      });

      //Flag for server removal
      await this.libraryStore.filesystemStartAssetDeletion(id, name);
    },

    deobfuscateArrayBuffer(arrayBuffer) {
      // Used for retrieving data from the caches API

      const dataView = new DataView(arrayBuffer);
      const uint8Array = new Uint8Array(arrayBuffer);
      const key = 0xab; // XOR encryption key

      // Apply XOR decryption to each byte of the data
      for (let i = 0; i < dataView.byteLength; i++) {
        uint8Array[i] ^= key;
      }

      return uint8Array.buffer;
    },

    setLoadingView(val) {
      this.isLoadingView = val;
    },

    async openSuperdocCompletedPdfPreview(data, linkObject, parentComponent = null) {
      this.setLoadingView(true);

      const link = linkObject();
      if (!link) return;

      const pdfUrl = `/asset?cb=${Date.now()}&id=${data.id}&agreelinkid=${link.id}`;
      const pdfFile = await fetch(pdfUrl);
      const pdfBlob = await pdfFile.blob();
      const pdfFileBlob = new File([pdfBlob], data.name, { type: 'application/pdf' });

      Vue.prototype.$openSuperdocViewerModal({
        linktitle: data.name,
        id: data.id,
        source: pdfFileBlob,
        headerCtas: [
          {
            id: 'download',
            label: 'Download',
            icon: 'file-arrow-down',
            type: 'secondary',
          },
        ],
        parent: parentComponent,
      });

      this.setLoadingView(false);
    },
  },
});
